import { useEffect } from 'react';
import { WorkspaceRoutes } from './user/pages/WorkspaceRoutes';
import { createBrowserRouter, Routes, Route, useLocation, useParams, Navigate, Outlet } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { Toast } from '@framework/ui/elements/Toast';
import { NotFound } from '@framework/ui/pages/NotFound';
import { LogoutPage } from 'LogoutPage';
import { AuthStateLoader, useIsPendingRegistration } from '@framework/auth';
import { HomePage } from '@user/pages/HomePage';
import { GroupPage } from '@user/pages/GroupPage';
import {
  GroupConsolePage,
  WithGroupConsoleMenu,
  GroupConsoleMembersPage,
  GroupConsoleMemberPage,
  GroupConsoleInvitePage,
  GroupConsoleInvitationsPage,
  GroupConsoleIpRestrictionPage,
} from '@user/pages/GroupConsole';
import { LoginPage } from '@user/pages/LoginPage';
import { NotFoundPage } from '@user/pages/NotFoundPage';
import { ReloadRedirect } from 'ReloadRedirect';
import { BuildInfo } from '@config/build-info';
import { EulaFooter } from '@user/EulaFooter';
import { ActionLogEventParamsRootContextProvider, useActionLogSender } from '@framework/action-log';
import { GroupMemberInvitationPage } from '@user/pages/GroupMemberInvitationPage';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { FirebaseSignUpPage } from '@user/pages/FirebaseSignUpPage';
import { ErrorBoundary } from '@framework/ui';
import { PasswordResetPage } from '@user/pages/PasswordResetPage';
import { MailAddressChangePage } from '@user/pages/MailAddressChangePage';
import { AccountConnectedPage } from '@user/pages/AccountConnectedPage';
import { ViewModelPageRouteContent } from '@user/pages/ViewModelPage';
import { UserMetadata } from '@user/UserMetadata';
import { PerfDebugModeSync } from '@user/PerfDebugModeSync';
import { GoogleLinkRedirectResultHandler } from '@user/GoogleLinkRedirectResultHandler';
import { MicrosoftLinkRedirectResultHandler } from '@user/MicrosoftLinkRedirectResultHandler';
import { WithSidebar } from '@user/Sidebar';
import { WithBookmark } from './group/Bookmark';
import {
  ReloadOnResume,
  WithReloadRequiredVersion,
  WithDebugMode,
  PersistActiveClientSession,
  WithMaintenancePage,
  WithVersionInfo,
  isDebugMode,
} from '@framework/utils';
import { FirebaseAnalyticsOperation } from '@framework/firebase/analytics';
import { CheckIp } from '@user/CheckIp';
import { SignUpRequestConfirmPage } from '@user/pages/SignUpRequestConfirmPage/SignUpRequestConfirmPage';
import { SignUpRequestDone } from '@user/pages/HomePage/SignUpRequestDone';
import { GroupConsoleLicensePage } from '@user/pages/GroupConsole/GroupConsoleLicensePage';
import { StopMailRequestPage } from '@user/pages/StopMailRequestPage/StopMailRequestPage';
import { TestHelloPage } from '@user/pages/TestHelloPage';
import { TagAssignedViewModelsPage } from '@user/pages/TagAssignedViewModelsPage/TagAssignedViewModelsPage';
import { WithGroupLicensePermissions } from './group/License/WithGroupLicensePermissions';

// メインレイアウト（ブックマーク、ライセンス権限、サイドバー）を構成するラッパーコンポーネント
const WithMainLayout: React.FC = () => {
  return (
    <WithBookmark>
      <WithGroupLicensePermissions>
        <WithSidebar>
          <Outlet />
        </WithSidebar>
      </WithGroupLicensePermissions>
    </WithBookmark>
  );
};

const usePageViews = () => {
  const location = useLocation();

  // Firebase Analytics のイベント記録
  useEffect(() => {
    FirebaseAnalyticsOperation.logEvent('page_view', {
      page_title: window.document.title,
      page_location: window.document.location.href,
      page_path: location.pathname,
      search: location.search,
      hash: location.hash,
      app_version: String(BuildInfo.version),
    });
  }, [location]);
};

const useActionLogPageViews = () => {
  const location = useLocation();
  const logSender = useActionLogSender();

  // 行動イベントログの記録
  useEffect(() => {
    logSender('global:page_view', {
      locationPathname: location.pathname,
      locationSearch: location.search,
      locationHash: location.hash,
      url: window.location.href,
    });
  }, [location, logSender]);
};

/**
 * ビューモデルページの旧パスから新パスへのリダイレクトコンポーネント
 */
const LegacyViewModelRedirect: React.FC = () => {
  const { viewModelId } = useParams<{ viewModelId: string }>();
  const location = useLocation();

  // viewModelId がない場合は NotFound を返す
  if (!viewModelId) return <NotFound />;

  return (
    <Navigate to={{ pathname: UserPagePathBuilder.viewModelPage(viewModelId), search: location.search }} replace />
  );
};

type PendingRegistrationGuardProps = {
  children: React.ReactNode;
};

/**
 * pendingRegistration(Firebase 認証済み・Balus ユーザー未作成)のときは、
 * 現在のパスに関わらず登録ページへ誘導するリダイレクトガード。
 * 登録ページ自身と authenticated 時は除外してループを防ぐ。
 *
 * リダイレクト時は children(ルーティング)を描画しないことで、
 * 遷移前ページの副作用(useEffect/データ取得)や一瞬の表示を防ぐ。
 */
const PendingRegistrationGuard: React.FC<PendingRegistrationGuardProps> = ({
  children,
}: PendingRegistrationGuardProps) => {
  const isPending = useIsPendingRegistration();
  const location = useLocation();
  const registrationPath = UserPagePathBuilder.firebaseSignUpPage();

  if (isPending && location.pathname !== registrationPath) {
    return <Navigate to={registrationPath} replace />;
  }
  return <>{children}</>;
};

/**
 * アプリケーションのルーティング定義コンポーネント
 */
const RouterContents: React.FC<unknown> = () => {
  // 内部で useLocation() を利用しており、 BrowserRouter のページ遷移を追跡する。
  usePageViews();
  useActionLogPageViews();

  return (
    <PendingRegistrationGuard>
      <Routes>
        {isDebugMode('test-trpc-hello') && <Route path="/test-hello" element={<TestHelloPage />} />}
        <Route path={UserPagePathBuilder.loginPage()} element={<LoginPage />} />
        <Route path={UserPagePathBuilder.firebaseSignUpPage()} element={<FirebaseSignUpPage />} />
        <Route path={UserPagePathBuilder.logoutPage()} element={<LogoutPage />} />
        <Route path={UserPagePathBuilder.passwordResetPage()} element={<PasswordResetPage />} />
        <Route path={UserPagePathBuilder.mailAddressChangeActionPage()} element={<MailAddressChangePage />} />
        <Route path={UserPagePathBuilder.signUpRequestConfirmPage()} element={<SignUpRequestConfirmPage />} />
        <Route path={UserPagePathBuilder.accountConnectedPage()} element={<AccountConnectedPage />} />
        <Route path={UserPagePathBuilder.groupMemberInvitationPage()} element={<GroupMemberInvitationPage />} />
        <Route path={UserPagePathBuilder.signUpRequestDonePage()} element={<SignUpRequestDone />} />
        <Route path={UserPagePathBuilder.stopMailRequestPage()} element={<StopMailRequestPage />} />
        <Route
          path={UserPagePathBuilder.tagAssignedViewModelsPage(':groupId', ':viewModelTagId')}
          element={
            <WithGroupLicensePermissions>
              <TagAssignedViewModelsPage />
            </WithGroupLicensePermissions>
          }
        />
        <Route
          path="/"
          element={
            <>
              <HomePage />
              <EulaFooter />
            </>
          }
        />
        {/* ビューモデルページの旧パスから新パスへのリダイレクト */}
        <Route
          path={UserPagePathBuilder.legacyViewModelPage(':workspaceID', ':viewModelId')}
          element={<LegacyViewModelRedirect />}
        />

        {/* グループコンソールルート - WithGroupConsoleMenu がネストルートの親 */}
        <Route path="/groups/:groupId/console/*" element={<WithGroupConsoleMenu />}>
          <Route path="" element={<GroupConsolePage />} />
          <Route path="license" element={<GroupConsoleLicensePage />} />
          <Route path="members/:memberId" element={<GroupConsoleMemberPage />} />
          <Route path="members" element={<GroupConsoleMembersPage />} />
          <Route path="invitations" element={<GroupConsoleInvitationsPage />} />
          <Route path="invite" element={<GroupConsoleInvitePage />} />
          <Route path="ip-restriction" element={<GroupConsoleIpRestrictionPage />} />
        </Route>

        {/* ユーザー向けの主なページ群 を使うルート */}
        <Route element={<WithMainLayout />}>
          <Route path={UserPagePathBuilder.groupPage(':groupId')} element={<GroupPage />} />
          <Route
            path={UserPagePathBuilder.workspaceFolderPage(':workspaceId', ':folderId')}
            element={<WorkspaceRoutes />}
          />
          <Route path={UserPagePathBuilder.workspacePage(':workspaceId')} element={<WorkspaceRoutes />} />
          <Route path={UserPagePathBuilder.viewModelPage(':viewModelId')} element={<ViewModelPageRouteContent />} />
        </Route>

        {/* React 管理外のパスに対して内部リダイレクトしたときに、適切にページ全体をリロードさせる */}
        <Route path="/api/*" element={<ReloadRedirect />} />
        <Route path="/admin/*" element={<ReloadRedirect />} />
        <Route path="/static/*" element={<ReloadRedirect />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </PendingRegistrationGuard>
  );
};

const RootLayout: React.FC = () => {
  return (
    <ErrorBoundary>
      <WithDebugMode>
        <AuthStateLoader>
          {(currentUserId) => (
            <>
              {/* ActionLogEventParamsRootContextProvider は、ログイン中ユーザ情報を利用するため、 */}
              {/* AuthStateLoader の内側に入れること */}
              <ActionLogEventParamsRootContextProvider>
                <WithVersionInfo>
                  <WithMaintenancePage>
                    <WithReloadRequiredVersion>
                      {/* 各ページのルーティング内容 */}
                      {/* ログイン中のユーザIDが変化したときには、各ページの状態(state)をリセットする */}
                      {/* https://beta.reactjs.org/learn/you-might-not-need-an-effect#resetting-all-state-when-a-prop-changes */}
                      <RouterContents key={currentUserId} />
                    </WithReloadRequiredVersion>

                    {/* このアプリケーションのタブがスリープから復帰したときにページの再読み込みを行う */}
                    <ReloadOnResume />
                  </WithMaintenancePage>
                </WithVersionInfo>
              </ActionLogEventParamsRootContextProvider>

              {/* ログイン中ユーザの情報を Sentry, Firebase Analytics に設定します */}
              <UserMetadata />
              {/* ?debug=perf のパフォーマンス計測機能を Levii ドメインのユーザにだけ有効化します */}
              <PerfDebugModeSync />
              <PersistActiveClientSession />
              <CheckIp currentUserId={currentUserId} />
              {/* Google アカウント連携(linkWithRedirect)からの復帰を検知して結果処理を行う */}
              {currentUserId && <GoogleLinkRedirectResultHandler />}
              {currentUserId && <MicrosoftLinkRedirectResultHandler />}
            </>
          )}
        </AuthStateLoader>
      </WithDebugMode>
      <Toast />
    </ErrorBoundary>
  );
};

const router = createBrowserRouter([
  {
    path: '*',
    element: <RootLayout />,
  },
]);

export const App: React.FC<unknown> = () => {
  return <RouterProvider router={router} />;
};
